// TestObservation (FNP-861) — the admin view of what the synthetic-user fleet
// actually observed.
//
// APPEND-ONLY. The executor owns createTestObservation and this module never
// creates or updates one — an observation is EVIDENCE and must not be rewritable.
// FNP-969 adds DELETE (discard the whole record), which is a different act from
// editing one; see services/mutations.ts for why it had to come from the console.
//
// Mirrors the testing-api schema exactly:
//   type TestObservation {
//     id: ID!  syntheticUserId: ID!  actionCode: String!  outcome: ObservationOutcome!
//     error: String  durationMs: Int  verificationResult: VerificationResult
//     dependedOn: [String]  testDefinitionId: ID  testRunIds: [ID]
//     observedAt: AWSDateTime!  createdByUsername: ID  created: AWSDateTime
//   }

export enum ObservationOutcome {
    PASS = 'PASS',
    FAIL = 'FAIL',
    SKIPPED = 'SKIPPED',
}

export enum VerificationResult {
    VERIFIED = 'VERIFIED',
    NOT_VERIFIED = 'NOT_VERIFIED',
    NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export type TestObservation = {
    id: string,
    syntheticUserId: string,
    // A HANDLE, not a script — an unregistered code is legal and produces an
    // `unimplemented` observation rather than an error (see testing-actions/types.ts).
    actionCode: string,
    outcome: ObservationOutcome,
    error?: string | null,
    durationMs?: number | null,
    verificationResult?: VerificationResult | null,
    // Present on a DEPENDENCY SKIP: the actionCodes this one waited on. The executor
    // stamps it when an upstream action failed, which is what lets the UI collapse a
    // cascade into one root cause + N deferrals instead of N equal-looking red rows.
    dependedOn?: (string | null)[] | null,
    // FNP-966. Which definition asked for this action.
    testDefinitionId?: string | null,
    // A LIST, deliberately. Browser session state is WAKE-scoped, so a given
    // actionCode executes AT MOST ONCE per wake and its single observation is
    // attributed to EVERY run that requested it. Never render this as one id.
    testRunIds?: (string | null)[] | null,
    observedAt: string,
    createdByUsername?: string,
    created?: string,
};

export type ListTestObservations = {
    listTestObservations: { items: TestObservation[], nextToken?: string }
}

export type GetTestObservation = {
    getTestObservation: TestObservation
}

export type ListTestObservationsBySyntheticUser = {
    listTestObservationsBySyntheticUser: { items: TestObservation[], nextToken?: string }
}

// deleteTestObservation returns the deleted id (`ID`), not the row — there is no
// row left to return.
export type DeleteTestObservation = {
    deleteTestObservation: string
}

// TestObservationsFilterInput — the five fields the backend actually supports.
// Anything not on this list must NOT be sent; AppSync rejects unknown input fields.
export type TestObservationsFilter = {
    syntheticUserId?: string,
    actionCode?: string,
    outcome?: string,
    verificationResult?: string,
    testDefinitionId?: string,
};
